import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { forkJoin, firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService, UserRole } from '@nebalkon-workspace/auth';
import {
  WarehouseCellResponse,
  WarehouseResponse,
  WarehouseService,
  WarehouseStatsResponse,
} from '@nebalkon-workspace/storage-api';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { StaffWarehouseCellGridComponent } from './staff-warehouse-cell-grid.component';
import { buildOccupiedCellIdSet } from './staff-warehouse-cell-grid.util';
import {
  isWarehouseUuid,
  warehouseRouteSegment,
} from '../utils/warehouse-route.util';
import { formatDashboardVolumeM3 } from '../home/staff-dashboard-warehouse.util';

@Component({
  selector: 'lib-staff-warehouse-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FormsModule, StaffWarehouseCellGridComponent],
  templateUrl: './staff-warehouse-detail.component.html',
  styleUrl: './staff-warehouse-detail.component.scss',
})
export class StaffWarehouseDetailComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #warehouseService = inject(WarehouseService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);
  readonly #auth = inject(AuthService);

  protected readonly warehouse = signal<WarehouseResponse | null>(null);
  protected readonly stats = signal<WarehouseStatsResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly isToggling = signal(false);
  protected readonly cells = signal<WarehouseCellResponse[]>([]);
  protected readonly occupiedCellIds = signal<ReadonlySet<string>>(new Set());
  protected readonly cellsLoading = signal(false);
  protected readonly cellsError = signal<string | null>(null);

  protected readonly hasWarehouseGrid = computed(
    () => (this.warehouse()?.rows?.length ?? 0) > 0,
  );

  protected readonly totalCells = computed(() => {
    const wh = this.warehouse();
    if (!wh?.rows) {
      return 0;
    }
    return wh.rows.reduce((sum, row) => sum + row.floors * row.columns, 0);
  });

  protected readonly canOpenPlacement = computed(() => {
    const roles = this.#auth.roles();
    return (
      roles.includes(UserRole.Admin) ||
      roles.includes(UserRole.Manager) ||
      roles.includes(UserRole.WarehouseWorker)
    );
  });

  protected readonly loadingPercentageLabel = computed(() => {
    const st = this.stats();
    if (!st) {
      return '0,00';
    }

    return formatDashboardVolumeM3(st.occupancy_percent ?? 0);
  });

  #routeWarehouseSegment(): string | null {
    return (
      this.#route.snapshot.paramMap.get('slug') ??
      this.#route.snapshot.paramMap.get('id')
    );
  }

  public ngOnInit(): void {
    const segment = this.#routeWarehouseSegment();
    if (!segment) {
      this.error.set(this.#translate.instant('admin.warehouses.detailNotFound'));
      this.loading.set(false);
      return;
    }

    if (isWarehouseUuid(segment)) {
      this.#loadWarehouseById(segment);
      return;
    }

    this.#loadWarehouseBySlug(segment);
  }

  #loadWarehouseBySlug(slug: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.#warehouseService.getWarehouseBySlug(slug).subscribe({
      next: (warehouse) => {
        this.warehouse.set(warehouse);
        this.#loadWarehouseStats(warehouse.id);
      },
      error: (err) => {
        console.error('Error loading warehouse by slug:', err);
        this.error.set(
          this.#translate.instant('admin.warehouses.detailLoadError'),
        );
        this.loading.set(false);
      },
    });
  }

  #loadWarehouseById(id: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.#warehouseService.getWarehouse(id).subscribe({
      next: (warehouse) => {
        this.warehouse.set(warehouse);
        this.#canonicalizeRoute(warehouse);
        this.#loadWarehouseStats(id);
      },
      error: (err) => {
        console.error('Error loading warehouse:', err);
        this.error.set(
          this.#translate.instant('admin.warehouses.detailLoadError'),
        );
        this.loading.set(false);
      },
    });
  }

  #canonicalizeRoute(warehouse: WarehouseResponse): void {
    const segment = warehouseRouteSegment(warehouse);
    const current = this.#routeWarehouseSegment();
    if (current === segment) {
      return;
    }

    void this.#router.navigate(
      [this.#cabinetPaths.staff, 'warehouses', segment],
      { replaceUrl: true },
    );
  }

  #loadWarehouseStats(id: string): void {
    this.#warehouseService.getWarehouseStats(id).subscribe({
      next: (stats) => {
        this.stats.set(stats);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading warehouse stats:', err);
        this.loading.set(false);
      },
    });

    this.#loadWarehouseCells(id);
  }

  #loadWarehouseCells(id: string): void {
    this.cellsLoading.set(true);
    this.cellsError.set(null);

    forkJoin({
      all: this.#warehouseService.getWarehouseCells(id),
      occupied: this.#warehouseService.getWarehouseCells(id, 'occupied'),
    }).subscribe({
      next: ({ all, occupied }) => {
        this.cells.set(all.data);
        this.occupiedCellIds.set(buildOccupiedCellIdSet(occupied.data));
        this.cellsLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading warehouse cells:', err);
        this.cells.set([]);
        this.occupiedCellIds.set(new Set());
        this.cellsError.set(
          this.#translate.instant('admin.warehouses.cells.loadError'),
        );
        this.cellsLoading.set(false);
      },
    });
  }

  protected async onToggleActive(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const newActiveState = target.checked;
    const wh = this.warehouse();

    if (!wh || this.isToggling()) {
      target.checked = !newActiveState;
      return;
    }

    this.isToggling.set(true);

    try {
      if (newActiveState) {
        await firstValueFrom(this.#warehouseService.activateWarehouse(wh.id));
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.warehouses.activateSuccess')],
        });
      } else {
        await firstValueFrom(
          this.#warehouseService.deactivateWarehouse(wh.id),
        );
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.warehouses.deactivateSuccess')],
        });
      }

      this.warehouse.set({
        ...wh,
        status: newActiveState ? 'active' : 'inactive',
      });
    } catch (toggleError) {
      console.error('Error toggling warehouse status:', toggleError);
      target.checked = !newActiveState;
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            newActiveState
              ? 'admin.warehouses.activateError'
              : 'admin.warehouses.deactivateError',
          ),
        ],
      });
    } finally {
      this.isToggling.set(false);
    }
  }

  protected onEdit(): void {
    const wh = this.warehouse();
    if (!wh) {
      return;
    }

    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      warehouseRouteSegment(wh),
      'edit',
    ]);
  }

  protected openPlacement(): void {
    const wh = this.warehouse();
    if (!wh) {
      return;
    }

    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      warehouseRouteSegment(wh),
      'placement',
    ]);
  }
}
